import type { ReactNode } from "react";
import { useMemo } from "react";
import { useSidebar, SidebarNavigation } from "@repo/core";
import type { NavigationItem } from "@repo/core";
import type { PortfolioType } from "@repo/api";

interface OverviewLayoutProps {
  children: ReactNode;
  portfolios: PortfolioType[];
  className?: string;
}

/**
 * Layout component for the Overview page
 * Handles sidebar navigation and main content area
 */
export const OverviewLayout = ({
  children,
  portfolios,
  className = "",
}: OverviewLayoutProps) => {
  const { isSidebarOpen, setSidebarOpen } = useSidebar();

  const navigationItems: NavigationItem[] = useMemo(
    () =>
      portfolios.map((portfolio) => ({
        href: `/${portfolio.modelId}`,
        label: portfolio.name,
      })),
    [portfolios]
  );

  return (
    <div className={`overview-page min-h-full w-full max-w-full ${className}`}>
      <div className="flex min-h-full relative w-full max-w-full">
        {/* Sidebar - hidden on small devices, toggleable */}
        <SidebarNavigation
          items={navigationItems}
          className={`
            w-64 flex-shrink-0 
            ${isSidebarOpen ? "block" : "hidden"} 
            lg:block 
            fixed lg:sticky 
            z-50 lg:z-auto 
            h-screen lg:h-screen
            top-0 lg:top-0
            left-0 lg:left-auto
            bg-white lg:bg-transparent
            shadow-lg lg:shadow-none
          `}
        />

        {/* Overlay for mobile */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content area */}
        <div className="flex-1 lg:ml-0 min-w-0">
          <div className="p-6 max-w-full">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default OverviewLayout;
